import { ref, computed, watch } from 'vue';
import { useInboxPlanVisibility } from '@/composables/useInboxPlanVisibility';
import { fetchInboxSummary, fetchWorkflowInbox } from '@/api/workflowApi';
import { isAnnualAppraisalsEnabled } from '@/constants/featureFlags';

function normalizeSummaryPayload(data) {
  if (isAnnualAppraisalsEnabled()) {
    const count = Number(data?.pending_item_count ?? 0);
    return {
      pendingCount: Number.isFinite(count) ? Math.max(0, count) : 0,
      breakdown:
        data?.breakdown && typeof data.breakdown === 'object' ? data.breakdown : {}
    };
  }

  const breakdown =
    data?.breakdown && typeof data.breakdown === 'object'
      ? { ...data.breakdown }
      : {};
  const appraisalCount = Number(breakdown.annual_appraisal ?? 0);
  delete breakdown.annual_appraisal;

  const rawCount = Number(data?.pending_item_count ?? 0);
  const pendingCount = Number.isFinite(rawCount)
    ? Math.max(0, rawCount - (Number.isFinite(appraisalCount) ? appraisalCount : 0))
    : Object.values(breakdown).reduce(
        (sum, value) => sum + (Number.isFinite(Number(value)) ? Number(value) : 0),
        0
      );

  return { pendingCount, breakdown };
}

/** Open workflows overseen by the actor; the scope remains meaningful at zero. */
function normalizeOpenSummary(data) {
  const scope = ['company', 'department'].includes(data?.open_scope) ? data.open_scope : null;
  const counts = data?.open_breakdown && typeof data.open_breakdown === 'object' ? { ...data.open_breakdown } : {};
  const hidden = !isAnnualAppraisalsEnabled() ? Number(counts.annual_appraisal || 0) : 0;
  if (!isAnnualAppraisalsEnabled()) delete counts.annual_appraisal;
  const count = Number(data?.open_count ?? 0) - (Number.isFinite(hidden) ? hidden : 0);
  return { openScope: scope, openCount: scope && Number.isFinite(count) ? Math.max(0, count) : 0, openBreakdown: counts };
}

const planVisibility = ref(false);
const pendingCount = ref(0);
const openCount = ref(0);
const openScope = ref(null);
const openBreakdown = ref({});
const completedCount = ref(null);
const waitingCount = ref(null);
const correctionCount = ref(null);
const breakdown = ref({});
const loadFailed = ref(false);

let inflightPromise = null;
let pendingInvalidation = false;
let generation = 0;

async function fetchSummary(seq) {
  try {
    const [data, historyData, waitingData, correctionData] = await Promise.all([
      fetchInboxSummary(),
      fetchWorkflowInbox({ tab: 'completed', page: 1, page_size: 1, due_state: 'all', include_development_plans: planVisibility.value }).catch(() => null),
      ...['waiting', 'needs_correction'].map((tab) => fetchWorkflowInbox({ tab, page: 1, page_size: 1, due_state: 'all', include_development_plans: planVisibility.value }).catch(() => null))
    ]);
    if (seq !== generation) return;
    const normalized = normalizeSummaryPayload(data);
    const open = normalizeOpenSummary(data);
    pendingCount.value = normalized.pendingCount;
    breakdown.value = normalized.breakdown;
    openCount.value = open.openCount;
    openScope.value = open.openScope;
    openBreakdown.value = open.openBreakdown;
    if (historyData && typeof historyData.total === 'number') {
      completedCount.value = Math.max(0, historyData.total);
    }
    if (waitingData && typeof waitingData.total === 'number') waitingCount.value = Math.max(0, waitingData.total);
    if (correctionData && typeof correctionData.total === 'number') correctionCount.value = Math.max(0, correctionData.total);
    loadFailed.value = false;
  } catch (error) {
    if (seq !== generation) return;
    loadFailed.value = true;
    console.warn('Failed to load inbox summary:', error);
  }
}

function runRefresh() {
  const seq = generation;
  const promise = (async () => {
    do {
      pendingInvalidation = false;
      await fetchSummary(seq);
    } while (seq === generation && pendingInvalidation);
  })().finally(() => {
    if (inflightPromise === promise) inflightPromise = null;
  });
  inflightPromise = promise;
  return inflightPromise;
}

/**
 * Module-level inbox summary state (sidebar badge + action invalidation).
 */
export function useInboxSummary() {
  const { showDevelopmentPlans } = useInboxPlanVisibility();
  watch(showDevelopmentPlans, (value) => { planVisibility.value = value; }, { immediate: true });
  const visiblePendingCount = computed(() => Math.max(0, pendingCount.value - (showDevelopmentPlans.value ? 0 : Number(breakdown.value.development_plan || 0))));
  const visibleOpenCount = computed(() => Math.max(0, openCount.value - (showDevelopmentPlans.value ? 0 : Number(openBreakdown.value.development_plan || 0))));
  watch(showDevelopmentPlans, () => { completedCount.value = null; waitingCount.value = null; correctionCount.value = null; invalidate(); });
  async function refresh() {
    return inflightPromise || runRefresh();
  }

  function invalidate() {
    // Ordinary readers share the current fetch. Only a mutation needs another
    // read when the response already in flight may predate that mutation.
    if (inflightPromise) pendingInvalidation = true;
    else void refresh();
  }

  function reset() {
    generation += 1;
    inflightPromise = null;
    pendingInvalidation = false;
    pendingCount.value = 0;
    openCount.value = 0;
    openScope.value = null;
    openBreakdown.value = {};
    completedCount.value = null;
    waitingCount.value = null;
    correctionCount.value = null;
    breakdown.value = {};
    loadFailed.value = false;
  }

  return {
    pendingCount: visiblePendingCount,
    openCount: visibleOpenCount,
    openScope,
    openBreakdown,
    completedCount,
    waitingCount,
    correctionCount,
    breakdown,
    loadFailed,
    refresh,
    invalidate,
    reset
  };
}
